import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Briefcase, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  Search,
  Filter,
  Bookmark,
  Send,
  User,
  Calendar,
  MapPin,
  DollarSign,
  Star
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    applications: 0,
    interviews: 0,
    offers: 0,
    savedJobs: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch dashboard stats
      const statsResponse = await axios.get('/api/student/dashboard/stats', { withCredentials: true });
      setStats(statsResponse.data);

      // Fetch recent job postings
      const jobsResponse = await axios.get('/api/jobs/recent', { withCredentials: true });
      setRecentJobs(jobsResponse.data);

      // Fetch recent applications
      const applicationsResponse = await axios.get('/api/student/applications/recent', { withCredentials: true });
      setApplications(applicationsResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'applied':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20';
      case 'shortlisted':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
      case 'interview':
        return 'text-purple-600 bg-purple-100 dark:bg-purple-900/20';
      case 'accepted':
        return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      case 'rejected':
        return 'text-red-600 bg-red-100 dark:bg-red-900/20';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-900/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'applied':
        return <Send className="w-4 h-4" />;
      case 'shortlisted':
        return <Star className="w-4 h-4" />;
      case 'interview':
        return <Calendar className="w-4 h-4" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-secondary-200 dark:bg-secondary-700 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-secondary-200 dark:bg-secondary-700 rounded-lg"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-secondary-200 dark:bg-secondary-700 rounded-lg"></div>
              <div className="h-96 bg-secondary-200 dark:bg-secondary-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400">
            Here's what's happening with your job applications and opportunities.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Briefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Applications
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.applications}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <Calendar className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Interviews
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.interviews}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Offers
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.offers}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Bookmark className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                  Saved Jobs
                </p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {stats.savedJobs}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white dark:bg-secondary-800 rounded-xl p-6 shadow-lg mb-8"
        >
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/student/jobs"
              className="flex items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
            >
              <Search className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3" />
              <span className="font-medium text-secondary-900 dark:text-white">
                Browse Jobs
              </span>
            </Link>
            <Link
              to="/student/applications"
              className="flex items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
            >
              <TrendingUp className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3" />
              <span className="font-medium text-secondary-900 dark:text-white">
                View Applications
              </span>
            </Link>
            <Link
              to="/student/profile"
              className="flex items-center p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
            >
              <User className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3" />
              <span className="font-medium text-secondary-900 dark:text-white">
                Update Profile
              </span>
            </Link>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Job Postings */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg"
          >
            <div className="p-6 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Recent Job Postings
                </h2>
                <Link
                  to="/student/jobs"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  View All
                </Link>
              </div>
            </div>
            <div className="p-6">
              {recentJobs.length > 0 ? (
                <div className="space-y-4">
                  {recentJobs.slice(0, 5).map((job) => (
                    <div
                      key={job.id}
                      className="p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">
                            {job.title}
                          </h3>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">
                            {job.company}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-secondary-500 dark:text-secondary-400">
                            <div className="flex items-center">
                              <MapPin className="w-3 h-3 mr-1" />
                              {job.location}
                            </div>
                            <div className="flex items-center">
                              <DollarSign className="w-3 h-3 mr-1" />
                              {job.salary}
                            </div>
                          </div>
                        </div>
                        <Link
                          to={`/student/jobs/${job.id}`}
                          className="ml-4 p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-200"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">
                  No recent job postings available.
                </p>
              )}
            </div>
          </motion.div>

          {/* Recent Applications */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-lg"
          >
            <div className="p-6 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Recent Applications
                </h2>
                <Link
                  to="/student/applications"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  View All
                </Link>
              </div>
            </div>
            <div className="p-6">
              {applications.length > 0 ? (
                <div className="space-y-4">
                  {applications.slice(0, 5).map((application) => (
                    <div
                      key={application.id}
                      className="p-4 border border-secondary-200 dark:border-secondary-700 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-secondary-900 dark:text-white mb-1">
                            {application.jobTitle}
                          </h3>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">
                            {application.company}
                          </p>
                          <div className="flex items-center space-x-2">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}
                            >
                              {getStatusIcon(application.status)}
                              <span className="ml-1">{application.status}</span>
                            </span>
                            <span className="text-xs text-secondary-500 dark:text-secondary-400">
                              Applied {new Date(application.appliedDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">
                  No applications yet. Start applying to jobs!
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
